import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, PackageCheck } from "lucide-react";
import api from "../lib/api";
import { formatApiErrorDetail } from "../lib/helpers";

export default function AdminReceive() {
  const { id } = useParams();
  const [state, setState] = useState("loading"); // loading | done | already | error
  const [sub, setSub] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/submissions/${id}`);
        if (!active) return;
        setSub(data);
        if (["received", "grading", "graded", "shipped"].includes(data.status)) {
          setState("already");
          return;
        }
        await api.patch(`/admin/submissions/${id}`, { status: "received" });
        if (!active) return;
        setState("done");
      } catch (e) {
        if (!active) return;
        setMsg(formatApiErrorDetail(e.response?.data?.detail) || "Could not mark this order as received.");
        setState("error");
      }
    })();
    return () => { active = false; };
  }, [id]);

  const short = (sub?.id || id).slice(-8);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="admin-receive">
      {state === "loading" && (
        <div className="flex flex-col items-center gap-4 text-neutral-500">
          <Loader2 className="animate-spin text-[#0A0A0A]" size={36} />
          <p className="font-semibold">Marking order #{short} as received…</p>
        </div>
      )}

      {state === "done" && (
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500 text-white flex items-center justify-center"><PackageCheck size={30} /></div>
          <h1 className="font-display font-900 text-3xl tracking-tight text-[#0A0A0A]" style={{ fontWeight: 900 }}>Package Received</h1>
          <p className="text-neutral-500">Order <strong className="text-[#0A0A0A]">#{short}</strong> is now marked <strong>Received</strong> and ready for grading.</p>
          <Link to="/admin" data-testid="receive-go-admin" className="mt-2 inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300">Open Fulfillment Console</Link>
        </div>
      )}

      {state === "already" && (
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#0A0A0A] text-white flex items-center justify-center"><CheckCircle2 size={30} /></div>
          <h1 className="font-display font-900 text-3xl tracking-tight text-[#0A0A0A]" style={{ fontWeight: 900 }}>Already Processed</h1>
          <p className="text-neutral-500">Order <strong className="text-[#0A0A0A]">#{short}</strong> is already at status <strong>{sub?.status}</strong>. No action taken.</p>
          <Link to="/admin" className="mt-2 inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300">Open Fulfillment Console</Link>
        </div>
      )}

      {state === "error" && (
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-500 border border-red-200 flex items-center justify-center"><AlertCircle size={30} /></div>
          <h1 className="font-display font-900 text-3xl tracking-tight text-[#0A0A0A]" style={{ fontWeight: 900 }}>Couldn't Receive</h1>
          <p className="text-neutral-500">{msg}</p>
          <Link to="/admin" className="mt-2 inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300">Open Fulfillment Console</Link>
        </div>
      )}
    </div>
  );
}
